import React from 'react';

/**
 * The one loader: Trodo's triangle in motion. One triangle made of four
 * facets, in the brand's ember; the three outer facets light one after the
 * other, clockwise from the top, and the centre one stays dim. Under it,
 * always in the same place, a short line saying what is on its way
 * ("Loading runs"), never a bare "Loading".
 *
 * It draws no shape of the page that is coming (no grey boxes, no accent
 * bar), so nothing flashes a layout the page will not have. Tables keep
 * their own row skeletons; this is for everything else.
 *
 *   size   'sm'  the mark alone, for a cell or a button
 *          'md'  a section: the mark with the label under it
 *          'lg'  a page
 *          'xl'  a hero, for a screen that says its own words (onboarding)
 *   label  what is loading; null for the mark alone, when the words are elsewhere
 *
 * `PageLoader` centres it in the content panel.
 */

const MARK = { sm: 14, md: 28, lg: 36, xl: 56 };      // height in px
const TEXT = { md: 'text-[12px]', lg: 'text-[13px]' };
const STEP = 0.3;                     // seconds between one facet and the next
const CYCLE = 1.8;

/** "Loading..." and "Loading…" both read as "Loading": the mark is the wait. */
const trim = (s) => String(s || '').replace(/(\.\.\.|…)\s*$/, '');

/** The facets of one triangle on an 80×72 grid: top, right, left light in turn; the centre stays dim. */
const FACETS = [
  '40,6 56,34 24,34',
  '56,34 72,62 40,62',
  '24,34 40,62 8,62',
];

function Mark({ px }) {
  return (
    <svg aria-hidden width={Math.round((px * 80) / 72)} height={px} viewBox="0 0 80 72" className="shrink-0 overflow-visible" style={{ fill: 'var(--trodo-primary)' }}>
      {FACETS.map((pts, i) => (
        <polygon key={pts} points={pts} className="trodo-mark-facet"
          style={{ animationDelay: `${i * STEP}s`, animationDuration: `${CYCLE}s` }} />
      ))}
      <polygon points="24,34 56,34 40,62" opacity=".18" />
      <style>{`
        .trodo-mark-facet { opacity: .18; animation-name: trodoFacet; animation-timing-function: ease-in-out; animation-iteration-count: infinite; }
        @keyframes trodoFacet { 0%, 100% { opacity: .18; } 20%, 40% { opacity: 1; } }
        @media (prefers-reduced-motion: reduce) {
          .trodo-mark-facet { animation: none; opacity: .7; }
        }
      `}</style>
    </svg>
  );
}

export default function BrandLoader({ label = 'Loading', size = 'md', className = '' }) {
  const text = trim(label) || 'Loading';
  if (size === 'sm' || label === null) {
    return <span role="status" aria-label={text} className={`inline-flex items-center ${className}`}><Mark px={MARK[size] || MARK.sm} /></span>;
  }
  return (
    <div role="status" aria-live="polite" aria-label={text}
      className={`inline-flex flex-col items-center justify-center gap-3 ${className}`} style={{ fontFamily: 'Inter, sans-serif' }}>
      <Mark px={MARK[size] || MARK.md} />
      <span className={`text-center font-medium text-[color:var(--text-muted)] ${TEXT[size] || TEXT.md}`}>{text}</span>
    </div>
  );
}

export function PageLoader({ label = 'Loading' }) {
  return (
    <div className="flex h-full min-h-[320px] w-full flex-1 items-center justify-center self-stretch">
      <BrandLoader size="lg" label={label} />
    </div>
  );
}
